import { useId } from "react";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  CircleHelp,
  Flag,
  Loader2,
  ShieldCheck,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import {
  PsvReviewOutcome,
  PsvReviewStatus,
} from "@api/shared-runtime/psv-review";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { OutcomePill } from "../source-result-row";
import type { ReportItem, RunReport, RunScope } from "../types";
import { ItemDetails } from "./item-details";
import {
  decisionAttribution,
  itemCheckOutcome,
  licenseSubtitle,
  itemTitle,
  showsIssueCallout,
  STAFF_STATUS_LABEL,
} from "./item-status";
import type { ItemDecisions } from "./use-item-decisions";

type Decision = NonNullable<ReportItem["decision"]>;

/** Quiet staff-status indicators: an icon and muted text, no border. */
const STAFF_STATUS: Partial<
  Record<PsvReviewStatus, { label: string; icon: LucideIcon; style: string }>
> = {
  [PsvReviewStatus.Accepted]: {
    label: STAFF_STATUS_LABEL[PsvReviewStatus.Accepted],
    icon: Check,
    style: "text-green-700/80",
  },
  [PsvReviewStatus.Concern]: {
    label: STAFF_STATUS_LABEL[PsvReviewStatus.Concern],
    icon: Flag,
    style: "text-red-700/80",
  },
  [PsvReviewStatus.InReview]: {
    label: STAFF_STATUS_LABEL[PsvReviewStatus.InReview],
    icon: Clock,
    style: "text-blue-700/80",
  },
};

const OUTCOME_ICONS: Record<
  PsvReviewOutcome,
  { icon: LucideIcon; style: string }
> = {
  [PsvReviewOutcome.Valid]: { icon: ShieldCheck, style: "text-green-600" },
  [PsvReviewOutcome.Issues]: { icon: TriangleAlert, style: "text-amber-600" },
  [PsvReviewOutcome.NotAvailable]: {
    icon: CircleHelp,
    style: "text-yellow-600",
  },
};

function StatusIcon({ item }: { item: ReportItem }) {
  const status = item.decision?.status;
  let Icon: LucideIcon = Loader2;
  let style = "text-blue-600 motion-safe:animate-spin";
  if (status === PsvReviewStatus.Accepted) {
    Icon = CheckCircle2;
    style = "text-green-600";
  } else if (status === PsvReviewStatus.Concern) {
    Icon = Flag;
    style = "text-red-600";
  } else if (item.outcome) {
    ({ icon: Icon, style } = OUTCOME_ICONS[item.outcome]);
  }
  return <Icon className={cn("size-5 shrink-0", style)} aria-hidden />;
}

function StaffStatus({ decision, id }: { decision: Decision; id: string }) {
  const presentation = STAFF_STATUS[decision.status];
  if (!presentation) {
    return <span aria-hidden />;
  }
  const Icon = presentation.icon;
  const decided = decision.status !== PsvReviewStatus.InReview;
  const attribution = decided ? decisionAttribution(decision) : null;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            id={id}
            className={cn(
              "inline-flex items-center gap-1 text-xs font-medium",
              presentation.style,
            )}
          >
            <Icon className="size-3.5 shrink-0" aria-hidden />
            {presentation.label}
          </span>
        </TooltipTrigger>
        {attribution && (
          <TooltipContent className="text-xs">{attribution}</TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

/** One item: its status and automated verdict on a single line, with the
 * evidence and decision controls in an expandable panel. */
export function ItemRow({
  item,
  checks,
  runId,
  scope,
  expanded,
  canEdit,
  decisions,
  uploadFocusRequested,
  onToggle,
  onUploadFocused,
  onAccept,
  onConcern,
  onUndo,
}: {
  item: ReportItem;
  checks: RunReport["checks"];
  runId: string;
  scope: RunScope;
  expanded: boolean;
  canEdit: boolean;
  decisions: ItemDecisions;
  uploadFocusRequested: boolean;
  onToggle: () => void;
  onUploadFocused: () => void;
  onAccept: () => void;
  onConcern: () => void;
  onUndo: () => void;
}) {
  const detailsId = useId();
  const statusId = useId();
  const decided =
    item.decision?.status === PsvReviewStatus.Accepted ||
    item.decision?.status === PsvReviewStatus.Concern;
  const title = itemTitle({ item, checks });
  let subtitle = licenseSubtitle({ item, checks });
  // Expanded, the issue banner states the verdict's description right below
  // the row, so the row does not repeat it.
  if (!subtitle && !(expanded && showsIssueCallout(item))) {
    subtitle = item.verdict?.description || null;
  }
  return (
    <article
      aria-label={`${title} result`}
      data-item-key={item.key}
      className="overflow-hidden rounded-xl border bg-background"
    >
      <button
        type="button"
        data-item-toggle
        aria-expanded={expanded}
        aria-controls={detailsId}
        aria-describedby={item.decision ? statusId : undefined}
        aria-label={`${expanded ? "Hide" : "View"} details for ${title}`}
        className="grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 p-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:grid-cols-[auto_minmax(0,1fr)_12rem_6.5rem_1rem] sm:px-4"
        onClick={onToggle}
      >
        <StatusIcon item={item} />
        <span className="min-w-0 space-y-0.5">
          <span
            className={cn(
              "block break-words text-sm",
              decided ? "font-medium text-muted-foreground" : "font-semibold",
            )}
          >
            {title}
          </span>
          {subtitle && (
            <span className="block text-xs leading-5 text-muted-foreground">
              {subtitle}
            </span>
          )}
        </span>
        <span className="col-start-2 row-start-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 sm:contents">
          <span className="sm:justify-self-start">
            <OutcomePill
              outcome={itemCheckOutcome(item)}
              inProgressLabel={item.verdict?.label ?? "In progress"}
            />
          </span>
          {item.decision ? (
            <StaffStatus decision={item.decision} id={statusId} />
          ) : (
            <span aria-hidden />
          )}
        </span>
        <ChevronDown
          className={cn(
            "col-start-3 row-start-1 size-4 shrink-0 text-muted-foreground transition-transform sm:col-auto sm:row-auto",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {expanded && (
        <ItemDetails
          id={detailsId}
          item={item}
          title={title}
          checks={checks}
          runId={runId}
          scope={scope}
          canEdit={canEdit}
          decisions={decisions}
          uploadFocusRequested={uploadFocusRequested}
          onUploadFocused={onUploadFocused}
          onAccept={onAccept}
          onConcern={onConcern}
          onUndo={onUndo}
        />
      )}
    </article>
  );
}
